import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState } from "../components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, MapPin, Trash2, Edit2, Mail, Phone, UserSquare2 } from "lucide-react";
import { toast } from "sonner";

const CONTACT_ROLES = ["Purchasing", "Sales", "Engineering", "Finance", "Logistics", "Owner / Director", "Other"];

const emptyContact = { name: "", role: "Purchasing", email: "", phone: "", notes: "" };
const emptyCustomerForm = { name: "", contact_person: "", email: "", phone: "", address: "", tax_no: "", notes: "" };
const emptyVisit = { date: new Date().toISOString().slice(0, 10), visited_by: "", location: "", summary: "", outcome: "" };

function VisitationDialog({ open, onOpenChange, customerId, initial, onSaved }) {
  const [v, setV] = useState(emptyVisit);
  useEffect(() => { if (open) setV({ ...emptyVisit, ...(initial || {}) }); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial?.id) {
        await api.put(`/customers/${customerId}/visitations/${initial.id}`, v);
        toast.success("Visitation updated");
      } else {
        await api.post(`/customers/${customerId}/visitations`, v);
        toast.success("Visitation logged");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{initial?.id ? "Edit Visitation" : "New Visitation"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="label-caps">Date</Label>
            <Input type="date" className="rounded-none mt-1" value={v.date || ""} onChange={(e) => setV({ ...v, date: e.target.value })} required data-testid="visit-date" /></div>
          <div><Label className="label-caps">Visited by</Label>
            <Input className="rounded-none mt-1" value={v.visited_by || ""} onChange={(e) => setV({ ...v, visited_by: e.target.value })} data-testid="visit-by" /></div>
          <div><Label className="label-caps">Location</Label>
            <Input className="rounded-none mt-1" value={v.location || ""} onChange={(e) => setV({ ...v, location: e.target.value })} data-testid="visit-location" /></div>
          <div><Label className="label-caps">Summary</Label>
            <Textarea rows={3} className="rounded-none mt-1" required value={v.summary || ""} onChange={(e) => setV({ ...v, summary: e.target.value })} data-testid="visit-summary" /></div>
          <div><Label className="label-caps">Outcome</Label>
            <Input className="rounded-none mt-1" value={v.outcome || ""} onChange={(e) => setV({ ...v, outcome: e.target.value })} data-testid="visit-outcome" /></div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="visit-save-btn">
              {initial?.id ? "Save changes" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditCustomerDialog({ open, onOpenChange, customer, onSaved }) {
  const [form, setForm] = useState(emptyCustomerForm);
  useEffect(() => { if (open && customer) setForm({ ...emptyCustomerForm, ...customer }); }, [open, customer]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/customers/${customer.id}`, form);
      toast.success("Customer updated");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Edit Customer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {[
            ["name", "Name", true],
            ["contact_person", "Primary contact"],
            ["email", "Email"],
            ["phone", "Phone"],
            ["tax_no", "Tax No."],
          ].map(([k, label, req]) => (
            <div key={k}>
              <Label className="label-caps">{label}</Label>
              <Input
                data-testid={`edit-customer-${k}`}
                className="rounded-none mt-1"
                required={!!req}
                value={form[k] || ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              />
            </div>
          ))}
          <div>
            <Label className="label-caps">Address</Label>
            <Textarea className="rounded-none mt-1" rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-customer-address" />
          </div>
          <div>
            <Label className="label-caps">Notes</Label>
            <Textarea className="rounded-none mt-1" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-customer-notes" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="edit-customer-save">Save changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContactFormDialog({ open, onOpenChange, customerId, initial, onSaved }) {
  const [form, setForm] = useState(emptyContact);
  useEffect(() => { if (open) setForm({ ...emptyContact, ...(initial || {}) }); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial?.id) {
        await api.put(`/customers/${customerId}/contacts/${initial.id}`, form);
        toast.success("Contact updated");
      } else {
        await api.post(`/customers/${customerId}/contacts`, form);
        toast.success("Contact added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save contact");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{initial?.id ? "Edit contact" : "Add contact"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="label-caps">Full name *</Label>
            <Input required autoFocus className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
          </div>
          <div>
            <Label className="label-caps">Role</Label>
            <select
              className="w-full h-9 border border-slate-300 rounded-none mt-1 px-2 text-sm bg-white"
              value={form.role || ""}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="contact-role"
            >
              {CONTACT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <Label className="label-caps">Email</Label>
            <Input type="email" className="rounded-none mt-1" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
          </div>
          <div>
            <Label className="label-caps">Phone</Label>
            <Input className="rounded-none mt-1" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
          </div>
          <div>
            <Label className="label-caps">Notes</Label>
            <Textarea rows={2} className="rounded-none mt-1" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-notes" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="contact-save">
              {initial?.id ? "Save changes" : "Add contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [visitOpen, setVisitOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);

  const load = () => api.get(`/customers/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <PageBody>Loading…</PageBody>;
  const { customer, orders, offers } = data;
  const contacts = customer.contacts || [];

  const delVisit = async (vid) => {
    if (!window.confirm("Delete this visitation?")) return;
    await api.delete(`/customers/${id}/visitations/${vid}`);
    load();
  };
  const openAddVisit = () => { setEditingVisit(null); setVisitOpen(true); };
  const openEditVisit = (v) => { setEditingVisit(v); setVisitOpen(true); };
  const delContact = async (contact) => {
    if (!window.confirm(`Remove contact ${contact.name}?`)) return;
    try {
      await api.delete(`/customers/${id}/contacts/${contact.id}`);
      toast.success("Contact removed");
      load();
    } catch { toast.error("Failed to remove"); }
  };
  const openAddContact = () => { setEditingContact(null); setContactOpen(true); };
  const openEditContact = (c) => { setEditingContact(c); setContactOpen(true); };

  return (
    <>
      <PageHeader
        title={customer.name}
        subtitle={<Link to="/customers" className="hover:text-blue-700">Customers</Link>}
        testid="customer-detail-title"
        actions={
          <Button
            onClick={() => setEditOpen(true)}
            variant="outline"
            className="rounded-none border-slate-300 hover:bg-slate-100"
            data-testid="edit-customer-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" /> Edit customer
          </Button>
        }
      />
      <PageBody>
        {/* Info card */}
        <div className="grid md:grid-cols-4 gap-0 border border-slate-300 bg-white mb-6" data-testid="customer-info">
          {[
            ["Primary contact", customer.contact_person || "-"],
            ["Email", customer.email || "-"],
            ["Phone", customer.phone || "-"],
            ["Tax No.", customer.tax_no || "-"],
          ].map(([k, val], i, arr) => (
            <div key={k} className={`p-5 ${i < arr.length - 1 ? "border-r border-slate-200" : ""}`}>
              <div className="label-caps">{k}</div>
              <div className="mt-2 text-slate-900 font-mono">{val}</div>
            </div>
          ))}
          {customer.address && (
            <div className="col-span-4 border-t border-slate-200 p-5">
              <div className="label-caps mb-2">Address</div>
              <div className="text-slate-800 whitespace-pre-line">{customer.address}</div>
            </div>
          )}
          {customer.notes && (
            <div className="col-span-4 border-t border-slate-200 p-5">
              <div className="label-caps mb-2">Notes</div>
              <div className="text-slate-700 whitespace-pre-line text-sm">{customer.notes}</div>
            </div>
          )}
        </div>

        <Tabs defaultValue="contacts">
          <TabsList className="rounded-none bg-transparent p-0 border-b border-slate-300 w-full justify-start">
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:shadow-none data-[state=active]:bg-transparent">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="visitations" data-testid="tab-visitations" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:shadow-none data-[state=active]:bg-transparent">Visitations ({customer.visitations?.length || 0})</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:shadow-none data-[state=active]:bg-transparent">Orders ({orders.length})</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:shadow-none data-[state=active]:bg-transparent">Offers ({offers.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="pt-6">
            <div className="flex justify-end mb-4">
              <Button
                onClick={openAddContact}
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="add-contact-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add contact
              </Button>
            </div>
            {contacts.length === 0 ? (
              <EmptyState title="No contacts yet" hint="Add every person you deal with at this customer — buyers, engineers, finance, etc." />
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {contacts.map((c) => (
                  <div key={c.id} className="bg-white border border-slate-300 p-4" data-testid={`contact-card-${c.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center">
                          <UserSquare2 className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-display text-lg text-slate-900 leading-tight">{c.name}</div>
                          <div className="label-caps text-blue-700">{c.role || "—"}</div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openEditContact(c)} className="text-slate-400 hover:text-blue-700" title="Edit" data-testid={`edit-contact-${c.id}`}><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => delContact(c)} className="text-slate-400 hover:text-red-600" title="Remove" data-testid={`del-contact-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1 text-sm font-mono text-slate-700">
                      {c.email && (
                        <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /> <a href={`mailto:${c.email}`} className="hover:text-blue-700">{c.email}</a></div>
                      )}
                      {c.phone && (
                        <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" /> <a href={`tel:${c.phone}`} className="hover:text-blue-700">{c.phone}</a></div>
                      )}
                      {!c.email && !c.phone && <div className="text-slate-400">No contact channels</div>}
                    </div>
                    {c.notes && <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-2 whitespace-pre-line">{c.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="visitations" className="pt-6">
            <div className="flex justify-end mb-4">
              <Button
                onClick={openAddVisit}
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="add-visitation-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Log Visitation
              </Button>
            </div>
            {(!customer.visitations || customer.visitations.length === 0) ? (
              <EmptyState title="No visitations logged" hint="Log customer visits to keep a history." />
            ) : (
              <div className="border-l-2 border-slate-300 pl-6 space-y-6">
                {customer.visitations.slice().reverse().map((v) => (
                  <div key={v.id} className="relative">
                    <div className="absolute -left-[27px] top-1 w-3 h-3 bg-slate-900" />
                    <div className="bg-white border border-slate-300 p-4" data-testid={`visit-card-${v.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="font-mono text-sm text-slate-900">{v.date}</div>
                          {v.location && <div className="flex items-center text-xs text-slate-500 gap-1"><MapPin className="w-3 h-3" /> {v.location}</div>}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditVisit(v)}
                            className="text-slate-400 hover:text-blue-700"
                            title="Edit"
                            data-testid={`edit-visit-${v.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => delVisit(v.id)}
                            className="text-slate-400 hover:text-red-600"
                            title="Delete"
                            data-testid={`del-visit-${v.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-slate-800 whitespace-pre-line">{v.summary}</div>
                      {v.outcome && <div className="mt-1 text-xs text-slate-500">Outcome: {v.outcome}</div>}
                      {v.visited_by && <div className="mt-1 label-caps">by {v.visited_by}</div>}
                      {v.updated_at && (
                        <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                          edited {v.updated_at.slice(0, 10)}{v.updated_by ? ` · ${v.updated_by}` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders" className="pt-6">
            {orders.length === 0 ? <EmptyState title="No orders" /> :
              <div className="bg-white border border-slate-300">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-300"><tr>
                    <th className="py-2 px-3 text-left label-caps">Order #</th>
                    <th className="py-2 px-3 text-left label-caps">Items</th>
                    <th className="py-2 px-3 text-left label-caps">Status</th>
                    <th className="py-2 px-3 text-left label-caps">Created</th>
                  </tr></thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono"><Link to={`/orders/${o.id}`} className="text-blue-700 hover:text-blue-900">{o.order_number}</Link></td>
                        <td className="py-2 px-3 font-mono">{o.items?.length || 0}</td>
                        <td className="py-2 px-3"><StatusBadge status={o.status} /></td>
                        <td className="py-2 px-3 font-mono text-slate-500">{o.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
          </TabsContent>

          <TabsContent value="offers" className="pt-6">
            {offers.length === 0 ? <EmptyState title="No offers" /> :
              <div className="bg-white border border-slate-300">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-300"><tr>
                    <th className="py-2 px-3 text-left label-caps">Offer #</th>
                    <th className="py-2 px-3 text-left label-caps">Title</th>
                    <th className="py-2 px-3 text-left label-caps">Items</th>
                    <th className="py-2 px-3 text-left label-caps">Status</th>
                    <th className="py-2 px-3 text-left label-caps">Created</th>
                  </tr></thead>
                  <tbody>
                    {offers.map((o) => (
                      <tr key={o.id} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono"><Link to={`/offers/${o.id}`} className="text-blue-700 hover:text-blue-900">{o.offer_number}</Link></td>
                        <td className="py-2 px-3">{o.title || "-"}</td>
                        <td className="py-2 px-3 font-mono">{o.items?.length || 0}</td>
                        <td className="py-2 px-3"><StatusBadge status={o.status} /></td>
                        <td className="py-2 px-3 font-mono text-slate-500">{o.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
          </TabsContent>
        </Tabs>

        <EditCustomerDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          customer={customer}
          onSaved={load}
        />
        <ContactFormDialog
          open={contactOpen}
          onOpenChange={setContactOpen}
          customerId={id}
          initial={editingContact}
          onSaved={load}
        />
        <VisitationDialog
          open={visitOpen}
          onOpenChange={setVisitOpen}
          customerId={id}
          initial={editingVisit}
          onSaved={load}
        />
      </PageBody>
    </>
  );
}
